/**
 * Flujo de caja proyectado - Finance 360 (S4-03, S4-04 y S4-05)
 *
 * La tabla por período es la vista principal (y la accesible): saldo inicial,
 * entradas (cobros de CxC y presupuesto no facturado), salidas (pagos de CxP,
 * presupuesto no facturado y cuotas de préstamos) y saldo final. Debajo, dos
 * gráficos en el mismo eje en colones: entradas contra salidas, y el saldo final
 * con los negativos en el color de alerta. La proyección la calcula el servidor
 * (services/flujoCajaService.js).
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ReferenceLine } from 'recharts';
import { AlertTriangle, Download, Save, RefreshCw, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import { LoadingPage } from '../../components/LoadingSpinner';

// Paleta validada (scripts/validate_palette.js del skill dataviz): azul y naranja,
// los dos primeros categóricos; el rojo es el de estado «crítico», solo para el negativo.
const COLOR = { entradas: '#2a78d6', salidas: '#eb6834', saldo: '#2a78d6', negativo: '#d03b3b' };
const hoy = () => new Date(Date.now() - 6 * 3600e3).toISOString().slice(0, 10);
const crc = (n, dec = 0) => { const v = Number(n) || 0; return `${v < 0 ? '−' : ''}₡${Math.abs(v).toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`; };
const corto = (n) => {
  const v = Math.abs(Number(n) || 0); const s = n < 0 ? '−' : '';
  return v >= 1e9 ? `${s}₡${(v / 1e9).toFixed(1)} mil M` : v >= 1e6 ? `${s}₡${(v / 1e6).toFixed(1)} M` : v >= 1e3 ? `${s}₡${(v / 1e3).toFixed(0)} mil` : `${s}₡${v.toFixed(0)}`;
};
const fecha = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '');

const FILAS = [
  ['Saldo inicial', 'saldoInicial', 'saldo'],
  ['Cobros de CxC', 'cobrosCxc', null, 'cobrosCxc'],
  ['Ingresos presupuestados no facturados', 'presupuestoIngresos'],
  ['Total entradas', 'entradas', 'total'],
  ['Pagos de CxP', 'pagosCxp', null, 'pagosCxp'],
  ['Egresos presupuestados no facturados', 'presupuestoEgresos'],
  ['Inversiones presupuestadas', 'inversiones'],
  ['Cuotas de préstamos', 'prestamos', null, 'prestamos'],
  ['Total salidas', 'salidas', 'total'],
  ['Flujo neto', 'neto', 'total'],
  ['Saldo final', 'saldoFinal', 'saldo']
];

export default function FlujoCaja() {
  const { currentEmisor } = useAuthStore();
  const [f, setF] = useState({ desde: hoy(), periodos: 6, tipoCambio: '', conPresupuesto: true });
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState('proyeccion');
  const [abierta, setAbierta] = useState(null);
  const params = useCallback(() => ({ desde: f.desde, periodos: f.periodos, tipoCambio: f.tipoCambio || undefined, conPresupuesto: f.conPresupuesto ? '1' : '0' }), [f]);

  const calcular = useCallback(async () => {
    setCargando(true);
    try { setDatos((await contabilidadApi.getFlujoCaja(params())).data.data); } catch { setDatos(null); } finally { setCargando(false); }
  }, [params]);
  useEffect(() => { if (currentEmisor) calcular(); }, [currentEmisor]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = async () => {
    const nombre = window.prompt('Nombre de la proyección', `Proyección del ${f.desde}`);
    if (nombre === null) return;
    try { await contabilidadApi.guardarFlujoCaja({ ...params(), nombre }); toast.success('Proyección guardada: después se compara contra el real'); } catch { /* aviso de la API */ }
  };
  const excel = async () => {
    try {
      const r = await contabilidadApi.exportarFlujoCaja(params());
      const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = `Flujo de caja ${f.desde}.xlsx`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { /* aviso */ }
  };

  if (cargando && !datos) return <LoadingPage />;
  const P = datos?.periodos || [];

  return (
    <div className="space-y-4" data-prueba="flujo-caja">
      <div className="flex flex-wrap items-end gap-3 bg-white p-4 rounded-xl border">
        <div className="mr-auto">
          <h1 className="text-lg font-semibold text-gray-900">Flujo de caja proyectado</h1>
          <p className="text-sm text-gray-500">Por período contable, en colones{datos?.tipoCambio ? ` · dólares al TC ${datos.tipoCambio}${datos.fechaTipoCambio ? ` (${String(datos.fechaTipoCambio).slice(0, 10)})` : ''}` : ''}.</p>
        </div>
        <label className="text-xs text-gray-600">Desde<input type="date" name="desde" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} className="block border rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-600">Períodos<input type="number" name="periodos" min="1" max="26" value={f.periodos} onChange={(e) => setF({ ...f, periodos: e.target.value })} className="block w-20 border rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-600">TC del dólar<input type="number" name="tipoCambio" step="0.01" min="0" placeholder={datos?.tipoCambio ? String(datos.tipoCambio) : 'el del día'} value={f.tipoCambio} onChange={(e) => setF({ ...f, tipoCambio: e.target.value })} className="block w-28 border rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2"><input type="checkbox" name="conPresupuesto" checked={f.conPresupuesto} onChange={(e) => setF({ ...f, conPresupuesto: e.target.checked })} /> Con presupuesto</label>
        <button onClick={calcular} className="inline-flex items-center gap-1 px-3 py-2 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 text-sm font-medium"><RefreshCw size={16} /> Calcular</button>
        <button onClick={guardar} className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"><Save size={16} /> Guardar</button>
        <button onClick={excel} className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"><Download size={16} /> Excel</button>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {[['proyeccion', 'Proyección'], ['real', 'Real contra proyectado']].map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={clsx('px-3 py-2 text-sm border-b-2 -mb-px', tab === k ? 'border-indigo-500 text-indigo-700 font-semibold' : 'border-transparent text-gray-500')}>{l}</button>)}
      </div>

      {tab === 'proyeccion' && datos && (
        <>
          {datos.avisos.map(a => (
            <div key={a} className={clsx('flex items-start gap-2 rounded-lg px-3 py-2 text-sm', /negativo/.test(a) ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800')} data-prueba={/negativo/.test(a) ? 'alerta-negativo' : 'aviso-flujo'}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{a}</span>
            </div>
          ))}
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 320 + P.length * 130 }} data-prueba="tabla-flujo">
              <thead className="bg-gray-50 text-[10.5px] font-semibold tracking-wider uppercase text-gray-500">
                <tr><th className="text-left px-3 py-2 sticky left-0 bg-gray-50">Colones</th>{P.map(p => <th key={p.periodo} className="text-right px-3 py-2 whitespace-nowrap">{p.periodo}<div className="font-normal normal-case text-gray-400">{fecha(p.desde)} – {fecha(p.hasta)}</div></th>)}</tr>
              </thead>
              <tbody>
                {FILAS.map(([rotulo, k, estilo, detalle]) => (
                  <Fragment key={k}>
                    <tr className={clsx(estilo === 'total' && 'font-semibold border-t', estilo === 'saldo' && 'font-semibold bg-indigo-50/40 border-t')}>
                      <td className={clsx("px-3 py-1.5 sticky left-0 whitespace-nowrap", estilo === "saldo" ? "bg-indigo-50" : "bg-white")}>
                        {detalle ? <button onClick={() => setAbierta(abierta === detalle ? null : detalle)} className="inline-flex items-center gap-1 text-left hover:text-indigo-700">{abierta === detalle ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{rotulo}</button> : rotulo}
                      </td>
                      {P.map(p => <td key={p.periodo} className={clsx('px-3 py-1.5 text-right font-mono', Number(p[k]) < 0 && 'text-red-600', k === 'saldoFinal' && p.negativo && 'bg-red-50')}>{Number(p[k]) === 0 && !estilo ? <span className="text-gray-300">–</span> : crc(p[k])}</td>)}
                    </tr>
                    {detalle && abierta === detalle && (
                      <tr><td colSpan={P.length + 1} className="px-3 py-2 bg-gray-50">
                        <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(P.length, 4)}, minmax(0, 1fr))` }} data-prueba={`detalle-${detalle}`}>
                          {P.map((p, i) => datos.detalle[detalle][i].length > 0 && (
                            <div key={p.periodo} className="text-xs"><div className="font-semibold text-gray-700 mb-1">{p.periodo}</div>
                              {datos.detalle[detalle][i].slice(0, 12).map((d, j) => <div key={j} className="flex justify-between gap-2"><span className="truncate">{d.documento}{d.tercero ? ` · ${d.tercero}` : ''}{d.vencido ? <span className="text-red-600"> (vencido)</span> : ''}</span><span className="font-mono">{crc(d.crc)}</span></div>)}
                              {datos.detalle[detalle][i].length > 12 && <div className="text-gray-400">y {datos.detalle[detalle][i].length - 12} más (en el Excel)</div>}
                            </div>
                          ))}
                        </div>
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {datos.presupuestos.length > 0 && <p className="text-xs text-gray-500">Presupuesto: {datos.presupuestos.map(x => `${x.anio} · ${x.escenario}${x.fueraDeCaja ? ` (deja fuera ${crc(x.fueraDeCaja)} de depreciación e intereses de préstamos)` : ''}`).join('; ')}. Lo ya registrado en la contabilidad sustituye al presupuesto de su período y cuenta.</p>}
          <Graficos periodos={P} />
        </>
      )}
      {tab === 'real' && <RealContraProyectado />}
    </div>
  );
}

function Graficos({ periodos }) {
  const datos = periodos.map(p => ({ periodo: p.periodo, Entradas: p.entradas, Salidas: p.salidas, saldo: p.saldoFinal, negativo: p.negativo }));
  const ejeY = { tickFormatter: corto, width: 80, tick: { fontSize: 11, fill: '#52514e' }, axisLine: false, tickLine: false };
  // Rótulo corto para que quepan todos: «Octubre 2026» → «Oct 26», «P03 2026» → «P03 26».
  const rotulo = (n) => { const [a, y] = String(n).split(' '); return `${/^P\d/.test(a) ? a : a.slice(0, 3)} ${String(y || '').slice(2)}`; };
  const ejeX = { dataKey: 'periodo', tick: { fontSize: 11, fill: '#52514e' }, axisLine: false, tickLine: false, interval: 0, tickFormatter: rotulo };
  const tip = { formatter: (v) => crc(v), cursor: { fill: 'rgba(0,0,0,0.04)' }, contentStyle: { fontSize: 12, borderRadius: 8 } };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-prueba="graficos-flujo">
      <div className="bg-white rounded-xl border p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Entradas y salidas por período</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={datos} barGap={2} barCategoryGap="25%" maxBarSize={40}>
            <CartesianGrid vertical={false} stroke="#ecebe7" />
            <XAxis {...ejeX} /><YAxis {...ejeY} />
            <Tooltip {...tip} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
            <Bar dataKey="Entradas" fill={COLOR.entradas} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="Salidas" fill={COLOR.salidas} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-white rounded-xl border p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Saldo de caja al final de cada período <span className="font-normal text-gray-500">(en rojo, negativo)</span></h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={datos} maxBarSize={48}>
            <CartesianGrid vertical={false} stroke="#ecebe7" />
            <XAxis {...ejeX} /><YAxis {...ejeY} />
            <ReferenceLine y={0} stroke="#8a8984" />
            <Tooltip {...tip} formatter={(v) => [crc(v), 'Saldo final']} />
            <Bar dataKey="saldo" radius={[4, 4, 0, 0]} isAnimationActive={false}>{datos.map(d => <Cell key={d.periodo} fill={d.negativo ? COLOR.negativo : COLOR.saldo} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RealContraProyectado() {
  const [lista, setLista] = useState(null);
  const [sel, setSel] = useState('');
  const [cmp, setCmp] = useState(null);
  const cargar = () => contabilidadApi.getFlujosGuardados().then(r => { setLista(r.data.data); if (!sel && r.data.data[0]) setSel(String(r.data.data[0].id)); }).catch(() => setLista([]));
  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sel) contabilidadApi.compararFlujoCaja(sel).then(r => setCmp(r.data.data)).catch(() => setCmp(null)); else setCmp(null); }, [sel]);
  const borrar = async () => { if (!window.confirm('¿Borrar esta proyección guardada?')) return; await contabilidadApi.eliminarFlujoGuardado(sel); setSel(''); cargar(); };
  if (!lista) return <LoadingPage />;
  if (!lista.length) return <div className="bg-white rounded-xl border p-8 text-center text-gray-500 text-sm">Todavía no hay proyecciones guardadas. Use «Guardar» en la proyección: cuando sus períodos pasen, aquí se comparan contra el movimiento real de caja y bancos.</div>;
  const fila = (rot, k) => (
    <tr key={k} className="border-t">
      <td className="px-3 py-1.5">{rot}</td>
      {cmp.periodos.map(p => (
        <Fragment key={p.periodo}>
          <td className="px-3 py-1.5 text-right font-mono text-gray-500">{crc(p.proyectado[k])}</td>
          <td className="px-3 py-1.5 text-right font-mono">{crc(p.real[k])}</td>
          <td className={clsx('px-3 py-1.5 text-right font-mono', p.diferencia[k] < 0 ? 'text-red-600' : p.diferencia[k] > 0 ? 'text-emerald-700' : 'text-gray-400')}>{crc(p.diferencia[k])}</td>
        </Fragment>
      ))}
    </tr>
  );
  return (
    <div className="space-y-3" data-prueba="real-vs-proyectado">
      <div className="flex items-center gap-2 text-sm">
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="border rounded-lg px-2 py-1.5" data-prueba="proyeccion-guardada">{lista.map(g => <option key={g.id} value={g.id}>{g.nombre} · desde el {g.desde}</option>)}</select>
        <button onClick={borrar} className="p-2 text-gray-400 hover:text-red-600" title="Borrar"><Trash2 size={16} /></button>
      </div>
      {cmp && (cmp.periodos.length === 0
        ? <div className="bg-white rounded-xl border p-6 text-sm text-gray-500">Ningún período de esta proyección empezó todavía.</div>
        : (
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 200 + cmp.periodos.length * 360 }} data-prueba="tabla-real">
              <thead className="bg-gray-50 text-[10.5px] font-semibold uppercase text-gray-500">
                <tr><th className="px-3 py-2 text-left" rowSpan={2}>Colones</th>{cmp.periodos.map(p => <th key={p.periodo} colSpan={3} className="px-3 py-2 text-center border-l">{p.periodo}{p.completo ? '' : ' (en curso)'}</th>)}</tr>
                <tr>{cmp.periodos.map(p => <Fragment key={p.periodo}><th className="px-3 py-1 text-right border-l">Proyectado</th><th className="px-3 py-1 text-right">Real</th><th className="px-3 py-1 text-right">Diferencia</th></Fragment>)}</tr>
              </thead>
              <tbody>{[['Entradas', 'entradas'], ['Salidas operativas', 'salidas'], ['Cuotas de préstamos', 'prestamos'], ['Saldo final', 'saldoFinal']].map(([r, k]) => fila(r, k))}</tbody>
            </table>
            {cmp.pendientes > 0 && <p className="px-3 py-2 text-xs text-gray-500">{cmp.pendientes} período(s) de la proyección todavía no empiezan.</p>}
          </div>
        ))}
    </div>
  );
}
